import { z } from 'zod';

/** Mirror of `apps/api/src/image/settings.ts`. The API is the authority; this keeps the editor's state typed. */
export const CropSchema = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
export type Crop = z.infer<typeof CropSchema>;

export const FORMATS = ['webp', 'jpeg', 'png'] as const;
export type Format = (typeof FORMATS)[number];

export const RenderSettingsSchema = z.object({
  rotate: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
  flip: z.boolean(),
  crop: CropSchema.nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  fidelity: z.number(),
  strip: z.boolean(),
  chroma420: z.boolean(),
  sharpen: z.boolean(),
  progressive: z.boolean(),
});
export type RenderSettings = z.infer<typeof RenderSettingsSchema>;
export type Rotation = RenderSettings['rotate'];

export const FIDELITY_MIN = 70;
export const FIDELITY_MAX = 98;
export const PNG_LOSSLESS_FROM = 95;

export const DEFAULT_SETTINGS: RenderSettings = { rotate: 0, flip: false, crop: null, width: 1200, height: null, fidelity: 87, strip: true, chroma420: true, sharpen: true, progressive: false };

export const FORMAT_LABEL: Record<Format, string> = { webp: 'WebP', jpeg: 'JPEG', png: 'PNG' };
export const FORMAT_EXT: Record<Format, string> = { webp: 'webp', jpeg: 'jpg', png: 'png' };
export const FORMAT_MIME: Record<Format, string> = { webp: 'image/webp', jpeg: 'image/jpeg', png: 'image/png' };

export const FORMAT_NOTE: Record<Format, string> = {
  webp: 'libwebp, method 6. The default for photographic content — smaller than JPEG at the same score, supported everywhere that matters.',
  jpeg: 'mozjpeg with trellis quantisation and optimised Huffman tables. The fallback in the picture element.',
  png: `libimagequant palette then max deflate; lossless at fidelity ${PNG_LOSSLESS_FROM}+. For flat art and screenshots only — a photograph in PNG is several times the size for no visible gain.`,
};

export const RATIOS = [
  { id: 'free', label: 'Free', r: 0 },
  { id: '16:9', label: '16:9', r: 16 / 9 },
  { id: '3:2', label: '3:2', r: 3 / 2 },
  { id: '4:3', label: '4:3', r: 4 / 3 },
  { id: '1:1', label: '1:1', r: 1 },
  { id: '3:4', label: '3:4', r: 3 / 4 },
] as const;
export type RatioId = (typeof RATIOS)[number]['id'];

/** "3:2" → 1.5; unknown → 0 (free). */
export function ratioValue(ratio: string | null | undefined): number {
  if (!ratio) return 0;
  const m = /^(\d+):(\d+)$/.exec(ratio);
  if (!m) return 0;
  const a = Number(m[1]), b = Number(m[2]);
  return a > 0 && b > 0 ? a / b : 0;
}

export function fidelityWord(f: number): string {
  return f < 78 ? 'visible loss' : f < 84 ? 'lean' : f < 90 ? 'web standard' : 'near-transparent';
}
